import React, { useState, useEffect } from 'react'

import { createPortal } from 'react-dom'
import { FiArrowLeft, FiSettings, FiX } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'

import { useDashboard } from '@/hooks'
import { getInsightDefinition, type InsightKey } from '@/insights/insights'
import type { InsightConfigurationType, InsightDefinition, InsightStatus } from '@/types/dashboard-types'

import { ChooseInsight } from './ChooseInsight'
import { ConfigureInsight } from './ConfigureInsight'

import Copy from '../config-panel-views/Copy'
import Export from '../config-panel-views/Export'
import Import from '../config-panel-views/Import'
import Landing from '../config-panel-views/Landing'
import MakeDefault from '../config-panel-views/MakeDefault'
import Permissions from '../config-panel-views/Permissions'
import Rename from '../config-panel-views/Rename'
import Share from '../config-panel-views/Share'

interface InsightAddPanelProps {
  isOpen: 'open' | 'closed' | 'add'
  onClose: (insightId?: string) => void
}

export interface AddInsightContext {
  configuration?: InsightConfigurationType
  step: 'Choose' | 'Configure' | 'Display'
}

export type PanelView =
  | 'Landing'
  | 'AddInsight'
  | 'RenameDashboard'
  | 'DefaultDashboard'
  | 'CopyDashboard'
  | 'ExportDashboard'
  | 'ImportDashboard'
  | 'ShareDashboard'
  | 'Permissions'

const DashboardConfigPanelContent: React.FC<InsightAddPanelProps> = ({ isOpen, onClose }) => {
  const dashboardContext = useDashboard()
  const [view, setView] = useState<PanelView | undefined>(undefined)
  const [context, setContext] = useState<AddInsightContext>({ step: 'Choose' })
  const dashboard = dashboardContext.currentDashboard
  const navigate = useNavigate()

  React.useEffect(() => {
    if (isOpen === 'add') {
      setView('AddInsight')
    } else if (isOpen === 'open') {
      setView('Landing')
    } else {
      setView(undefined)
    }
  }, [isOpen])

  const handleClose = () => {
    setContext({ step: 'Choose' })
    onClose()
  }

  const handleChoose = React.useCallback((slug: string) => {
    // Goofy way to let typescript know we are dealing with a specific Insight
    const _definition = getInsightDefinition(slug as InsightKey)
    type DataType = Exclude<Awaited<ReturnType<typeof _definition.load>>, InsightStatus>
    type ParamsType = Parameters<typeof _definition.configuration.isValid>[0]
    const definition = _definition as InsightDefinition<ParamsType, DataType>
    const configuration = dashboardContext.buildInsight(definition)
    setContext({ configuration, step: 'Configure' })
  }, [])

  const save = React.useCallback(() => {
    dashboardContext.addInsight({ dashboardSlug: dashboard.slug, insightConfiguration: context.configuration! })
    setContext({ configuration: context.configuration, step: 'Choose' })
    onClose(context.configuration!.id)
  }, [context.configuration, dashboard, dashboardContext])

  const nextAction = React.useMemo(() => {
    if (!context.configuration) {
      return { text: '', action: () => null, disabled: false, title: '' }
    }
    const _definition = getInsightDefinition(context.configuration.slug as InsightKey) as
      | ReturnType<typeof getInsightDefinition>
      | undefined
    // A stored slug can outlive its insight (retired or withheld); nothing to configure then.
    if (!_definition) {
      return { text: '', action: () => null, disabled: true, title: '' }
    }
    type DataType = Exclude<Awaited<ReturnType<typeof _definition.load>>, InsightStatus>
    type ParamsType = Parameters<typeof _definition.configuration.isValid>[0]
    const definition = _definition as InsightDefinition<ParamsType, DataType>
    const parameters = context.configuration!.parameters as ParamsType
    /*
    const params: ParamsType = parameters
    if (entity) {
      definition.configuration.controls.forEach((c) => {
        if (c.autofill === 'company') {
          if (companyId) {
            params[c.id] = companyId as ParamsType[typeof c.id]
          }
        } else if (entity && c.autofill === entity.type) {
          params[c.id] = entity.id as ParamsType[typeof c.id]
        }
      })
    }
    */
    const isValid = definition.configuration.isValid(
      parameters as Parameters<typeof definition.configuration.isValid>[0]
    )
    if (isValid === true) {
      if (definition.configuration.displayControls) {
        return { text: 'Next', action: () => setContext({ ...context, step: 'Display' }), disabled: false, title: '' }
      }
      return { text: 'Add to Dashboard', action: save, disabled: false, title: '' }
    }
    return {
      text: definition.configuration.displayControls ? 'Next' : 'Add to Dashboard',
      action: () => null,
      disabled: true,
      title: typeof isValid === 'string' ? isValid : undefined,
    }
  }, [context.configuration, save])

  const back = () => {
    if (context.step === 'Display') {
      setContext({ ...context, step: 'Configure' })
    } else if (context.step === 'Configure') {
      setContext({ ...context, step: 'Choose' })
    } else {
      setView('Landing')
    }
  }

  if (isOpen === 'closed') {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute right-0 top-0 h-full w-full sm:w-3/4 md:w-7/12 lg:w-1/2 xl:w-2/5 2xl:w-1/3 max-w-[500px] bg-base-100 shadow-xl border-l border-base-300 flex flex-col max-h-dvh">
        {/* PANEL TITLE */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-base-300">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <button
              onClick={back}
              className={`btn btn-ghost btn-sm btn-square mr-2 ${view === 'Landing' ? 'hidden' : 'flex'}`}
            >
              <FiArrowLeft className="w-4 h-4" />
            </button>
            <FiSettings className="w-5 h-5" />
            Dashboard Settings: {dashboard.title}
          </h2>
          <button onClick={handleClose} className="btn btn-ghost btn-sm btn-square">
            <FiX className="w-4 h-4" />
          </button>
        </div>
        {/* PANEL CONTENT */}
        <div className="flex-1 overflow-auto min-h-0">
          {view === 'Landing' && <Landing onSelectedView={setView} onClose={handleClose} />}
          {view === 'AddInsight' && (
            <div className="flex flex-col h-full ">
              {context.step === 'Choose' && <ChooseInsight onChoose={handleChoose} />}
              {context.step === 'Configure' && <ConfigureInsight context={context} setContext={setContext} />}
            </div>
          )}
          {view === 'RenameDashboard' && <Rename onClose={() => setView('Landing')} />}
          {view === 'DefaultDashboard' && <MakeDefault onClose={() => setView('Landing')} />}
          {view === 'CopyDashboard' && (
            <Copy
              onClose={() => setView('Landing')}
              onSuccess={(slug: string) => {
                setView('Landing')
                navigate(`/dashboards/${slug}`)
                setTimeout(onClose, 1000)
              }}
            />
          )}
          {view === 'ExportDashboard' && <Export onClose={() => setView('Landing')} />}
          {view === 'ImportDashboard' && (
            <Import
              onClose={() => setView('Landing')}
              onSuccess={(slug: string) => {
                setView('Landing')
                navigate(`/dashboards/${slug}`)
                setTimeout(onClose, 1000)
              }}
            />
          )}
          {view === 'ShareDashboard' && <Share />}
          {view === 'Permissions' && <Permissions onClose={() => setView('Landing')} />}
        </div>
        {view === 'AddInsight' && context.step === 'Configure' && (
          <div className="border-t border-base-300 p-4 flex gap-2 justify-end flex-shrink-0">
            <button onClick={handleClose} className="btn btn-ghost">
              Cancel
            </button>
            {nextAction.text && (
              <div data-tip={nextAction.title} className="tooltip tooltip-left">
                <button
                  onClick={nextAction.action}
                  className="btn btn-primary"
                  disabled={nextAction.disabled}
                  title={nextAction.title}
                >
                  {nextAction.text}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export const DashboardConfigPanel: React.FC<InsightAddPanelProps> = (props) => {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // Use portal to render outside of any grid container's stacking context
  if (!mounted) return null

  return createPortal(<DashboardConfigPanelContent {...props} />, document.body)
}
